import { createContext, useContext } from 'react';
import { type User } from '@mastery/api-client-web';

export type ExitPermit = { release: () => void };
export type ExitGuard = () => Promise<ExitPermit | false>;
export type SessionStatus = 'checking' | 'authenticated' | 'anonymous' | 'unavailable';
type Session = {
  user: User;
  sessionStatus: SessionStatus;
  checkSession: () => Promise<SessionStatus>;
  reauthenticating: boolean;
  refresh: () => Promise<void>;
  updateUser: (u: User) => void;
  signedOut: (notice: string) => void;
  registerExitGuard: (guard: ExitGuard) => () => void;
};
export type LoginRequest = {
  reason?: 'answer' | 'submit' | 'manual' | 'expired';
  resume?: { kind: 'answer' | 'submit'; questionId?: string };
  onSuccess?: () => void;
};
export type Auth = {
  user?: User;
  requestLogin: (request?: LoginRequest) => void;
};
export const SessionContext = createContext<Session | null>(null);
export const AuthContext = createContext<Auth>({
  user: undefined,
  requestLogin: () => {},
});
export const useOptionalSession = () => useContext(SessionContext);
export const useAuth = () => useContext(AuthContext);
export function useSession() {
  const session = useOptionalSession();
  if (!session) throw new Error('This page requires a learning session');
  return session;
}
